export interface HelpArticleSection {
 h: string;
 p: string;
}

export interface HelpArticleFaq {
 q: string;
 a: string;
}

export interface HelpArticle {
 slug: string;
 title: string;
 description: string;
 intro: string;
 sections: HelpArticleSection[];
 faqs: HelpArticleFaq[];
 updated: string; // ISO date
}

export const HELP_ARTICLES: HelpArticle[] = [
 {
 slug: "how-to-find-a-primary-care-doctor",
 title: "How to Find a Primary Care Doctor (Step-by-Step Guide)",
 description:
 "A clear, practical guide to choosing a primary care doctor (PCP), including how to check insurance, compare reviews, and book your first visit.",
 intro:
 "A primary care doctor (PCP) is the person you see for checkups, common illnesses, prescription refills, and referrals to specialists. Picking the right one matters: most people stay with their PCP for years. This guide walks through exactly how to find one that fits your insurance, your location, and your needs.",
 sections: [
 {
 h: "1. Decide what kind of primary care doctor you need",
 p: "Most adults see a Family Medicine or Internal Medicine doctor. Family Medicine treats every age, kids, parents, and grandparents. Internal Medicine (Internists) focuses on adults and complex conditions. If you're choosing for a child, look for a Pediatrician. Women may also use an OB-GYN as their primary doctor for preventive care.",
 },
 {
 h: "2. Confirm the doctor accepts your insurance",
 p: "Before anything else, check your insurance card and filter by your plan. Even doctors that say 'accepts most insurance' may be out-of-network for your specific plan, which can mean hundreds of dollars in surprise bills. On Heallexa you can filter by insurer (Aetna, Blue Cross Blue Shield, UnitedHealthcare, Cigna, Humana, Kaiser, Medicare and more) before viewing results.",
 },
 {
 h: "3. Filter by location and availability",
 p: "Pick a doctor within a 15 20 minute drive of home or work, convenience is the #1 reason people skip checkups. Check whether the office offers evening hours, weekend slots, or telehealth visits if your schedule is tight.",
 },
 {
 h: "4. Read reviews and verify credentials",
 p: "Look for patterns across reviews rather than one-off complaints. Strong signals: short wait times, doctors who listen, clear explanations, easy prescription refills. Verify the doctor is board-certified in their specialty, every Heallexa profile shows credentials and license status.",
 },
 {
 h: "5. Book a 'meet and greet' or first appointment",
 p: "Many practices accept new patients for a short introductory visit. Use it to ask about typical wait times, after-hours care, and how they handle referrals. If it doesn't feel right, switching is normal, you don't owe a doctor your loyalty.",
 },
 ],
 faqs: [
 {
 q: "How do I find a primary care doctor near me?",
 a: "Search by your ZIP code and filter by 'Family Medicine' or 'Internal Medicine' plus your insurance. Heallexa's search returns providers ranked by distance, reviews, and verified status.",
 },
 {
 q: "Do I need a referral to see a primary care doctor?",
 a: "No. You can book a primary care visit directly. Referrals are only needed when your PCP sends you to a specialist (and only on certain insurance plans like HMOs).",
 },
 {
 q: "How often should I see my primary care doctor?",
 a: "Healthy adults typically have one annual physical. People managing chronic conditions like diabetes or high blood pressure usually see their PCP every 3 6 months.",
 },
 ],
 updated: "2026-05-18",
 },
 {
 slug: "how-to-find-a-doctor",
 title: "How to Find a Doctor: A Complete Guide for 2026",
 description:
 "Find a doctor that fits your needs in 5 steps, from picking the right specialty to verifying credentials and reading honest reviews.",
 intro:
 "Finding a doctor isn't just Googling 'doctor near me' and clicking the first result. The right doctor takes your insurance, treats your specific condition, and is someone you'll actually keep seeing. Here's how to find one in five steps.",
 sections: [
 {
 h: "1. Identify the specialty you need",
 p: "Routine care, checkups, common illnesses → Primary Care (Family Medicine or Internal Medicine). Skin issues → Dermatologist. Heart concerns → Cardiologist. Mental health → Psychiatrist or Psychologist. Women's health → OB-GYN. When in doubt, start with primary care, they'll refer you if needed.",
 },
 {
 h: "2. Match the doctor to your insurance",
 p: "An in-network doctor can save you thousands per year. Always filter by your specific insurance plan before booking. If you're uninsured, look for sliding-scale clinics or doctors who offer cash-pay rates.",
 },
 {
 h: "3. Check credentials and experience",
 p: "Board certification means the doctor passed rigorous exams in their specialty. Years in practice matters less than recent training in your specific condition. Every Heallexa profile shows board certification, medical school, and license status.",
 },
 {
 h: "4. Read reviews critically",
 p: "Look for at least 20+ reviews to spot real patterns. Focus on what matters to you: wait times, bedside manner, treatment success, communication. One angry review usually isn't a red flag, ten complaints about the same thing is.",
 },
 {
 h: "5. Book and evaluate the first visit",
 p: "Pay attention to how the office runs: easy scheduling, reasonable wait, doctor listens without rushing, clear next steps. If anything feels off, it's fine to keep looking. You're hiring a doctor, not signing a contract.",
 },
 ],
 faqs: [
 {
 q: "What's the easiest way to find a good doctor?",
 a: "Start with your insurance's in-network search to narrow the list, then filter by specialty and read reviews. Heallexa lets you do all three in one search.",
 },
 {
 q: "How do I find a doctor that's accepting new patients?",
 a: "On Heallexa, provider profiles indicate whether they're accepting new patients. You can also call the office directly, receptionists confirm availability immediately.",
 },
 {
 q: "Should I see a doctor online or in person for the first visit?",
 a: "For routine concerns (cold, refill, rash), telehealth is fine. For a new primary care relationship or anything physical (heart, joint, exam-based), book in person.",
 },
 ],
 updated: "2026-05-18",
 },
 {
 slug: "how-to-find-a-doctor-in-your-network",
 title: "How to Find a Doctor In Your Insurance Network",
 description:
 "Going in-network can save you thousands. Here's how to confirm a doctor accepts your specific insurance plan before you book.",
 intro:
 "'In-network' means your insurance company has a negotiated contract with that doctor. Out-of-network visits can cost 3 5x more, sometimes thousands of dollars per appointment. Here's how to make sure you stay in-network.",
 sections: [
 {
 h: "1. Look at your insurance card",
 p: "Your card shows the exact plan name (e.g. 'Blue Cross PPO,' 'Aetna HMO,' 'UnitedHealthcare Choice Plus'). 'I have Blue Cross' isn't enough, networks vary plan to plan. Note the plan name before searching.",
 },
 {
 h: "2. Filter by your specific plan, not just the insurer",
 p: "On Heallexa, you can filter by both insurer and plan type. A doctor who takes Aetna PPO may not take Aetna Medicare Advantage. Always pick the most specific filter that matches your card.",
 },
 {
 h: "3. Confirm directly with the office",
 p: "Even verified network lists get out of date. Call the office and ask: 'Are you in-network with [exact plan name]?' Get the answer in writing (an email or patient portal message) if possible.",
 },
 {
 h: "4. Watch for 'tiered' networks",
 p: "Many plans have preferred (Tier 1) and non-preferred (Tier 2) in-network doctors. Both are in-network, but Tier 2 costs more. Your insurance member portal lists tiers.",
 },
 {
 h: "5. Know what to do if no one in-network has openings",
 p: "If the wait is too long, ask your insurer about 'network gap exceptions', they can sometimes approve in-network rates for an out-of-network doctor when no in-network options are available within a reasonable distance.",
 },
 ],
 faqs: [
 {
 q: "How do I know if a doctor is in my insurance network?",
 a: "Filter by your insurance plan on Heallexa, then confirm with the doctor's office by phone. Don't rely on the insurer's online directory alone, it's often outdated.",
 },
 {
 q: "What happens if I see a doctor that's out-of-network?",
 a: "You'll usually pay full price up front and submit for partial reimbursement. Out-of-pocket costs are typically 3 5x higher than in-network visits.",
 },
 {
 q: "Can I appeal if my insurance refuses to cover a doctor?",
 a: "Yes. Every insurer has an appeals process, and 'network adequacy' rules require them to cover out-of-network at in-network rates when no in-network doctor is available.",
 },
 ],
 updated: "2026-05-18",
 },
 {
 slug: "how-to-find-a-doctor-that-takes-your-insurance",
 title: "How to Find a Doctor That Takes Your Insurance",
 description:
 "The fastest way to find doctors who accept your specific insurance plan, without spending an hour on the phone.",
 intro:
 "Calling 10 different offices to ask 'do you take my insurance?' is exhausting. There's a faster way. Here's how to find doctors who accept your plan in under five minutes.",
 sections: [
 {
 h: "1. Search by insurance, not just specialty",
 p: "On Heallexa, the insurance filter is the first thing you should set, before specialty, before location. This filters out every doctor who doesn't accept your plan, so you only see real options.",
 },
 {
 h: "2. Use your exact plan name",
 p: "Generic insurer names ('Blue Cross') return broader results. Plan-specific filters ('Blue Cross PPO' or 'Anthem Blue Cross HMO') give you accurate results. Your insurance card lists the exact plan.",
 },
 {
 h: "3. Look for the 'verified' insurance badge",
 p: "Claimed Heallexa listings show insurance acceptance verified by the provider themselves. Unverified listings rely on public data, which can be 6 12 months out of date.",
 },
 {
 h: "4. Call to confirm before booking",
 p: "Once you've shortlisted 2 3 doctors, call each office: 'I have [exact plan name], are you accepting new patients and currently in-network with this plan?' Two questions, 30 seconds each.",
 },
 {
 h: "5. Save your shortlist",
 p: "Bookmark your top 2 3 in-network doctors. When you need a referral, urgent appointment, or a second opinion later, you'll already have a vetted list ready.",
 },
 ],
 faqs: [
 {
 q: "How can I find doctors that take Medicare near me?",
 a: "Filter Heallexa search by 'Medicare' and your ZIP code. Most primary care doctors accept Medicare, but specialists vary, always confirm with the office.",
 },
 {
 q: "Are doctor directory insurance filters reliable?",
 a: "Directory data is a starting point, not a guarantee. Always confirm with the office directly before your first visit. Verified Heallexa listings are the most accurate because providers update them themselves.",
 },
 {
 q: "What if no doctor in my area takes my insurance?",
 a: "Expand your search radius, ask your insurer about telehealth-only providers, or request a 'network gap exception' to see an out-of-network doctor at in-network rates.",
 },
 ],
 updated: "2026-05-18",
 },
 {
 slug: "how-to-book-a-doctors-appointment",
 title: "How to Book a Doctor's Appointment (Online or by Phone)",
 description:
 "A simple walkthrough for booking a doctor's appointment online, by phone, or for someone else, plus what to bring to your visit.",
 intro:
 "Booking a doctor's appointment used to mean a phone call during business hours and 10 minutes on hold. Today, most appointments can be booked online in two minutes. Here's the step-by-step.",
 sections: [
 {
 h: "1. Find a doctor that fits your needs",
 p: "Search by specialty, location, and insurance on Heallexa. Once you've shortlisted a provider, open their profile to see availability, accepted plans, and patient reviews.",
 },
 {
 h: "2. Book online when possible",
 p: "Most modern practices offer online booking, pick a time slot, fill in your details, and confirm. You'll get a confirmation email and (usually) a reminder text 24 hours before. Online bookings are typically open 24/7.",
 },
 {
 h: "3. Call if the office doesn't have online booking",
 p: "Have ready: your full name, date of birth, insurance card, reason for the visit, and your availability. Most calls take under 5 minutes. Best time to call: 9 11 AM or 2 4 PM when phones are less busy.",
 },
 {
 h: "4. Booking for someone else (spouse, parent, child)",
 p: "You can usually book for a spouse or your own child. For another adult (like an elderly parent), the office may need verbal consent from them or HIPAA authorization on file. Be ready to provide their insurance info and date of birth.",
 },
 {
 h: "5. What to bring to your appointment",
 p: "Photo ID, insurance card, list of current medications, and a short list of questions or symptoms. Arrive 15 minutes early for new-patient paperwork.",
 },
 ],
 faqs: [
 {
 q: "Can I book a doctor's appointment online?",
 a: "Yes. Most practices accept online bookings 24/7. On Heallexa, providers with online booking show available time slots directly on their profile.",
 },
 {
 q: "Can someone else book a doctor's appointment for me?",
 a: "Yes, but for adults the office may need your verbal consent or HIPAA authorization. For a spouse, parent booking for a child, or an authorized caregiver, it's usually straightforward.",
 },
 {
 q: "How do I book an urgent or same-day doctor's appointment?",
 a: "Filter Heallexa by 'urgent care' or 'accepting same-day' providers. Many primary care offices also reserve same-day slots, call as early as possible in the morning.",
 },
 {
 q: "Can I book a doctor's appointment on a Saturday?",
 a: "Many practices offer Saturday hours, especially urgent care clinics and large group practices. Filter by 'weekend hours' on Heallexa to find them.",
 },
 ],
 updated: "2026-05-18",
 },
];

export function getHelpArticle(slug: string): HelpArticle | undefined {
 return HELP_ARTICLES.find((a) => a.slug === slug);
}
